import { useState, useEffect, useMemo, type ReactNode } from "react";
import {
  Search,
  ExternalLink,
  Building2,
  Users,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  HelpCircle,
  PauseCircle,
  PlayCircle,
  LogIn,
  RefreshCw,
  AlertCircle,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import type { TenantListItem } from "@platform/shared";

// ---------------------------------------------------------------------------
// Demo fallback data — shown when API is unavailable
// ---------------------------------------------------------------------------
const DEMO_TENANTS: ReadonlyArray<TenantListItem> = [
  {
    id: "tenant_001",
    name: "פרחים בעיר",
    slug: "flowers-city",
    domain: "flowers-city.co.il",
    status: "active",
    subscriptionAmount: 29900,
    health: {
      tenantId: "tenant_001",
      checkedAt: "2026-03-28T08:00:00Z",
      overall: "healthy",
      checks: { siteUp: true, d1Responsive: true, waBridge: true, sslValid: true },
    },
    createdAt: "2025-11-01T10:00:00Z",
  },
  {
    id: "tenant_002",
    name: "חנות הטעימות של דניאל",
    slug: "daniel-bakery",
    domain: null,
    status: "active",
    subscriptionAmount: 19900,
    health: {
      tenantId: "tenant_002",
      checkedAt: "2026-03-28T08:00:00Z",
      overall: "healthy",
      checks: { siteUp: true, d1Responsive: true, waBridge: null, sslValid: true },
    },
    createdAt: "2025-12-15T09:30:00Z",
  },
  {
    id: "tenant_003",
    name: "סלון יופי גלית",
    slug: "galit-salon",
    domain: "galit-beauty.co.il",
    status: "active",
    subscriptionAmount: 14900,
    health: {
      tenantId: "tenant_003",
      checkedAt: "2026-03-28T08:00:00Z",
      overall: "degraded",
      checks: { siteUp: true, d1Responsive: true, waBridge: false, sslValid: true },
    },
    createdAt: "2026-01-03T14:00:00Z",
  },
  {
    id: "tenant_004",
    name: "מסעדת הדגים של רוני",
    slug: "roni-fish",
    domain: null,
    status: "suspended",
    subscriptionAmount: 19900,
    health: null,
    createdAt: "2025-10-20T11:00:00Z",
  },
  {
    id: "tenant_005",
    name: "חנות אופניים של משה",
    slug: "moshe-bikes",
    domain: "moshe-bikes.co.il",
    status: "active",
    subscriptionAmount: 29900,
    health: {
      tenantId: "tenant_005",
      checkedAt: "2026-03-28T08:00:00Z",
      overall: "healthy",
      checks: { siteUp: true, d1Responsive: true, waBridge: true, sslValid: true },
    },
    createdAt: "2026-02-10T08:45:00Z",
  },
  {
    id: "tenant_006",
    name: "סטודיו צילום רונית",
    slug: "ronit-studio",
    domain: null,
    status: "active",
    subscriptionAmount: 14900,
    health: {
      tenantId: "tenant_006",
      checkedAt: "2026-03-28T08:00:00Z",
      overall: "healthy",
      checks: { siteUp: true, d1Responsive: true, waBridge: null, sslValid: true },
    },
    createdAt: "2026-03-01T16:20:00Z",
  },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatNIS(agorot: number): string {
  return `₪${(agorot / 100).toLocaleString("he-IL", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })}`;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("he-IL", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function getPlanFromAmount(agorot: number): {
  readonly label: string;
  readonly variant: "outline" | "info" | "default";
} {
  if (agorot >= 29900) return { label: "Enterprise", variant: "default" };
  if (agorot >= 19900) return { label: "Pro", variant: "info" };
  return { label: "Basic", variant: "outline" };
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type HealthLevel = "healthy" | "degraded" | "down";
type StatusLevel = "active" | "suspended" | "deleted";
type FilterStatus = "all" | StatusLevel;

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function HealthIndicator({
  health,
}: {
  readonly health: TenantListItem["health"];
}): ReactNode {
  if (!health) {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
        <HelpCircle className="h-3.5 w-3.5" />
        לא ידוע
      </span>
    );
  }

  const config: Record<
    HealthLevel,
    { icon: ReactNode; label: string; className: string }
  > = {
    healthy: {
      icon: <CheckCircle2 className="h-3.5 w-3.5 text-green-500" />,
      label: "תקין",
      className: "text-green-700 dark:text-green-400",
    },
    degraded: {
      icon: <AlertTriangle className="h-3.5 w-3.5 text-yellow-500" />,
      label: "חלקי",
      className: "text-yellow-700 dark:text-yellow-400",
    },
    down: {
      icon: <XCircle className="h-3.5 w-3.5 text-red-500" />,
      label: "לא זמין",
      className: "text-red-700 dark:text-red-400",
    },
  };

  const c = config[health.overall];
  return (
    <span className={cn("inline-flex items-center gap-1.5 text-xs font-medium", c.className)}>
      {c.icon}
      {c.label}
    </span>
  );
}

function StatusDot({ status }: { readonly status: StatusLevel }): ReactNode {
  const config: Record<StatusLevel, { dot: string; label: string; text: string }> = {
    active: {
      dot: "bg-green-500",
      label: "פעיל",
      text: "text-green-700 dark:text-green-400",
    },
    suspended: {
      dot: "bg-orange-500",
      label: "מושהה",
      text: "text-orange-700 dark:text-orange-400",
    },
    deleted: {
      dot: "bg-red-500",
      label: "נמחק",
      text: "text-red-700 dark:text-red-400",
    },
  };

  const v = config[status];
  return (
    <span className={cn("inline-flex items-center gap-1.5 text-xs font-medium", v.text)}>
      <span className={cn("h-2 w-2 rounded-full", v.dot, status === "active" && "shadow-[0_0_4px_1px] shadow-green-400/60")} />
      {v.label}
    </span>
  );
}

function TenantCard({
  tenant,
}: {
  readonly tenant: TenantListItem;
}): ReactNode {
  const plan = getPlanFromAmount(tenant.subscriptionAmount);
  const isSuspended = tenant.status === "suspended";

  return (
    <Card
      className={cn(
        "group relative overflow-hidden transition-all duration-200 hover:shadow-lg hover:-translate-y-0.5",
        isSuspended && "opacity-75",
      )}
    >
      {/* Top accent strip */}
      <div
        className={cn(
          "absolute inset-x-0 top-0 h-0.5",
          tenant.health?.overall === "healthy" && "bg-gradient-to-r from-green-400 to-emerald-500",
          tenant.health?.overall === "degraded" && "bg-gradient-to-r from-yellow-400 to-orange-400",
          tenant.health?.overall === "down" && "bg-gradient-to-r from-red-400 to-rose-500",
          !tenant.health && "bg-gradient-to-r from-gray-300 to-gray-400",
        )}
      />

      <CardContent className="p-5">
        {/* Header row: icon + name + plan badge */}
        <div className="flex items-start gap-3">
          <div className={cn(
            "flex h-10 w-10 shrink-0 items-center justify-center rounded-lg",
            isSuspended
              ? "bg-muted"
              : "bg-gradient-to-br from-primary/15 to-indigo-500/15",
          )}>
            <Building2 className={cn(
              "h-5 w-5",
              isSuspended ? "text-muted-foreground" : "text-primary",
            )} />
          </div>

          <div className="min-w-0 flex-1">
            <div className="flex items-center justify-between gap-2">
              <h3 className="truncate text-base font-bold leading-tight text-foreground">
                {tenant.name}
              </h3>
              <Badge variant={plan.variant} className="shrink-0 text-[10px]">
                {plan.label}
              </Badge>
            </div>

            {/* Subdomain */}
            <p
              className="mt-0.5 font-mono text-xs text-muted-foreground"
              dir="ltr"
            >
              {tenant.domain ?? `${tenant.slug}.listora.co.il`}
            </p>
          </div>
        </div>

        {/* Status row */}
        <div className="mt-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <StatusDot status={tenant.status} />
            <HealthIndicator health={tenant.health} />
          </div>
          <span className="text-xs text-muted-foreground">
            {formatDate(tenant.createdAt)}
          </span>
        </div>

        {/* Pricing */}
        <div className="mt-3 rounded-lg bg-muted/50 px-3 py-2">
          <span className="text-sm font-semibold tabular-nums text-foreground">
            {formatNIS(tenant.subscriptionAmount)}
          </span>
          <span className="text-xs text-muted-foreground"> /חודש</span>
        </div>

        {/* Action buttons */}
        <div className="mt-4 flex items-center gap-2 border-t pt-4">
          <a
            href={`https://${tenant.slug}.listora.co.il/admin`}
            target="_blank"
            rel="noopener noreferrer"
            className={cn(
              "inline-flex min-h-[44px] flex-1 items-center justify-center gap-2 rounded-lg",
              "bg-primary/10 text-primary hover:bg-primary/20 active:bg-primary/30",
              "text-sm font-medium transition-colors",
            )}
          >
            <LogIn className="h-4 w-4" />
            כניסה לאדמין
          </a>
          <button
            type="button"
            className={cn(
              "inline-flex min-h-[44px] flex-1 items-center justify-center gap-2 rounded-lg border",
              "text-sm font-medium transition-colors",
              isSuspended
                ? "border-green-200 bg-green-50 text-green-700 hover:bg-green-100 dark:border-green-800 dark:bg-green-950/30 dark:text-green-400"
                : "border-orange-200 bg-orange-50 text-orange-700 hover:bg-orange-100 dark:border-orange-800 dark:bg-orange-950/30 dark:text-orange-400",
            )}
          >
            {isSuspended ? (
              <>
                <PlayCircle className="h-4 w-4" />
                הפעל
              </>
            ) : (
              <>
                <PauseCircle className="h-4 w-4" />
                השהה
              </>
            )}
          </button>
          <a
            href={`/super/tenants/${tenant.id}`}
            className={cn(
              "inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-lg border",
              "text-muted-foreground hover:bg-muted hover:text-foreground transition-colors",
            )}
            title="פרטים"
          >
            <ExternalLink className="h-4 w-4" />
          </a>
        </div>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Skeleton card
// ---------------------------------------------------------------------------

function TenantCardSkeleton(): ReactNode {
  return (
    <Card className="overflow-hidden animate-pulse">
      <div className="absolute inset-x-0 top-0 h-0.5 bg-muted" />
      <CardContent className="p-5 space-y-3">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-lg bg-muted shrink-0" />
          <div className="flex-1 space-y-2">
            <div className="h-4 w-3/4 rounded bg-muted" />
            <div className="h-3 w-1/2 rounded bg-muted" />
          </div>
        </div>
        <div className="h-3 w-1/3 rounded bg-muted" />
        <div className="h-8 w-full rounded-lg bg-muted" />
        <div className="h-10 w-full rounded-lg bg-muted" />
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Filter button group
// ---------------------------------------------------------------------------

interface FilterButtonProps {
  readonly active: boolean;
  readonly onClick: () => void;
  readonly children: ReactNode;
}

function FilterButton({ active, onClick, children }: FilterButtonProps): ReactNode {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "min-h-[44px] rounded-lg px-4 text-sm font-medium transition-colors",
        active
          ? "bg-primary text-primary-foreground shadow-sm"
          : "bg-muted text-muted-foreground hover:bg-muted/80 hover:text-foreground",
      )}
    >
      {children}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Empty state
// ---------------------------------------------------------------------------

function EmptyState({ noTenants }: { readonly noTenants?: boolean }): ReactNode {
  return (
    <div className="col-span-2 flex flex-col items-center gap-4 rounded-xl border border-dashed bg-muted/20 py-16 text-center">
      <div className="flex h-16 w-16 items-center justify-center rounded-full bg-muted">
        <Users className="h-8 w-8 text-muted-foreground" />
      </div>
      <div>
        {noTenants ? (
          <>
            <p className="text-sm font-medium text-foreground">אין דיירים עדיין</p>
            <p className="mt-1 text-xs text-muted-foreground">
              הקם את הדייר הראשון בדף ההקמה
            </p>
          </>
        ) : (
          <>
            <p className="text-sm font-medium text-foreground">לא נמצאו תוצאות</p>
            <p className="mt-1 text-xs text-muted-foreground">
              נסה לשנות את מונחי החיפוש או הפילטרים
            </p>
          </>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function SuperTenantsPage(): ReactNode {
  const [tenants, setTenants] = useState<ReadonlyArray<TenantListItem>>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [usingDemo, setUsingDemo] = useState(false);

  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState<FilterStatus>("all");

  const fetchTenants = () => {
    setLoading(true);
    setError(null);

    fetch("/super/api/tenants", { credentials: "include" })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json() as Promise<{ success: boolean; data: TenantListItem[] }>;
      })
      .then((json) => {
        if (!json.success) throw new Error("API error");
        setTenants(json.data);
        setUsingDemo(false);
      })
      .catch(() => {
        setTenants(DEMO_TENANTS);
        setUsingDemo(true);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchTenants();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    return tenants.filter((t) => {
      if (
        q &&
        !t.name.toLowerCase().includes(q) &&
        !t.slug.toLowerCase().includes(q)
      ) {
        return false;
      }
      if (filterStatus !== "all" && t.status !== filterStatus) return false;
      return true;
    });
  }, [tenants, search, filterStatus]);

  const activeCount = tenants.filter((t) => t.status === "active").length;
  const suspendedCount = tenants.filter((t) => t.status === "suspended").length;

  return (
    <div className="space-y-6 p-4 md:p-6" dir="rtl">
      {/* Page header */}
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold tracking-tight">דיירים</h1>
          {!loading && (
            <Badge variant="info" className="text-xs">
              {tenants.length} סה&quot;כ
            </Badge>
          )}
          {usingDemo && (
            <Badge variant="warning" className="text-xs">
              נתוני דמו — שרת לא זמין
            </Badge>
          )}
        </div>
        {!loading && (
          <p className="text-sm text-muted-foreground">
            {activeCount} פעילים · {suspendedCount} מושהים
          </p>
        )}
      </div>

      {/* Error banner */}
      {error && (
        <div className="flex items-center gap-3 rounded-xl border border-destructive/40 bg-destructive/5 px-4 py-3">
          <AlertCircle className="h-4 w-4 text-destructive shrink-0" />
          <span className="text-sm text-destructive flex-1">{error}</span>
          <Button size="sm" variant="outline" onClick={fetchTenants} className="gap-1.5 h-8">
            <RefreshCw className="h-3.5 w-3.5" />
            נסה שנית
          </Button>
        </div>
      )}

      {/* Search + filters */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search className="absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground pointer-events-none" />
          <Input
            placeholder="חיפוש לפי שם או slug..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="ps-9 min-h-[44px]"
          />
        </div>

        {/* Filter button group */}
        <div className="flex items-center gap-2 rounded-xl bg-muted/50 p-1">
          <FilterButton
            active={filterStatus === "all"}
            onClick={() => setFilterStatus("all")}
          >
            הכל ({tenants.length})
          </FilterButton>
          <FilterButton
            active={filterStatus === "active"}
            onClick={() => setFilterStatus("active")}
          >
            פעיל ({activeCount})
          </FilterButton>
          <FilterButton
            active={filterStatus === "suspended"}
            onClick={() => setFilterStatus("suspended")}
          >
            מושהה ({suspendedCount})
          </FilterButton>
        </div>
      </div>

      {/* Results count */}
      {(search !== "" || filterStatus !== "all") && !loading ? (
        <p className="text-xs text-muted-foreground">
          מציג {filtered.length} מתוך {tenants.length} דיירים
        </p>
      ) : null}

      {/* Cards grid */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {loading ? (
          <>
            <TenantCardSkeleton />
            <TenantCardSkeleton />
            <TenantCardSkeleton />
            <TenantCardSkeleton />
          </>
        ) : filtered.length === 0 ? (
          <EmptyState noTenants={tenants.length === 0} />
        ) : (
          filtered.map((t) => <TenantCard key={t.id} tenant={t} />)
        )}
      </div>
    </div>
  );
}
